import React, { useState, useEffect } from 'react';
import logo from '../assets/logo.png';
import { useParams, Link } from 'react-router-dom';
import { 
  ArrowLeft, 
  FileText, 
  BookOpen, 
  Zap, 
  FileCheck, 
  FolderOpen,
  Eye,
  Calendar,
  GraduationCap,
  Archive,
  Users,
  Award,
  Target,
  Search,
  X,
  Layers,
  Filter
} from 'lucide-react';

function DepartmentPage() {
  const { deptName } = useParams();
  const [department, setDepartment] = useState(null);
  const [levels, setLevels] = useState([]);
  const [resources, setResources] = useState([]);
  const [filteredResources, setFilteredResources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedLevel, setSelectedLevel] = useState('all');
  const [selectedType, setSelectedType] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [showFilters, setShowFilters] = useState(false);

  const departmentData = {
    'INFO': { name: 'Informatique', fullName: 'Département Informatique', icon: '💻', color: 'mint', description: 'Formation en sciences informatiques, algorithmique, programmation, systèmes et réseaux.', students: 1240, professors: 45, labs: 8 },
    'MATH': { name: 'Mathématiques', fullName: 'Département Mathématiques', icon: '📐', color: 'pink', description: 'Formation en mathématiques fondamentales, analyse, algèbre, probabilités et statistiques.', students: 890, professors: 38, labs: 4 },
    'TIC': { name: 'TIC', fullName: 'Département TIC', icon: '📡', color: 'yellow', description: 'Technologies de l\'Information et de la Communication', students: 756, professors: 32, labs: 6 },
    'EEA': { name: 'EEA', fullName: 'Département Électronique', icon: '⚡', color: 'orange', description: 'Électronique, Électrotechnique et Automatique', students: 623, professors: 28, labs: 7 },
    'CPI1': { name: 'CPI1', fullName: 'Cycle Préparatoire 1ère année', icon: '🎯', color: 'mint', description: 'Première année du cycle préparatoire', students: 320, professors: 25, labs: 4 },
    'CPI2': { name: 'CPI2', fullName: 'Cycle Préparatoire 2ème année', icon: '🎯', color: 'mint', description: 'Deuxième année du cycle préparatoire', students: 310, professors: 24, labs: 4 },
    'Cycle preparatoire 1': { name: 'CPI1', fullName: 'Cycle Préparatoire 1ère année', icon: '🎯', color: 'mint', description: 'Première année du cycle préparatoire', students: 320, professors: 25, labs: 4 },
    'Cycle preparatoire 2': { name: 'CPI2', fullName: 'Cycle Préparatoire 2ème année', icon: '🎯', color: 'mint', description: 'Deuxième année du cycle préparatoire', students: 310, professors: 24, labs: 4 },
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        // Find department info
        let deptInfo = departmentData[deptName];
        if (!deptInfo) {
          deptInfo = departmentData[deptName?.toUpperCase()];
        }
        if (!deptInfo) {
          const found = Object.values(departmentData).find(d => d.name === deptName);
          deptInfo = found;
        }
        setDepartment(deptInfo);
        
        // Fetch resources
        const resourcesRes = await fetch('https://unwavering-luck-2076e754ea.strapiapp.com/api/resources?populate[subject][populate][level][populate][degree][populate]=program');
        const subjectsRes = await fetch('https://unwavering-luck-2076e754ea.strapiapp.com/api/subjects?populate=level');
        const levelsRes = await fetch('https://unwavering-luck-2076e754ea.strapiapp.com/api/levels?populate=degree');
        
        const resourcesData = await resourcesRes.json();
        const subjectsData = await subjectsRes.json();
        const levelsData = await levelsRes.json();
        
        const allResources = resourcesData.data || [];
        const allSubjects = subjectsData.data || [];
        const allLevelsData = levelsData.data || [];
        
        // Filter approved resources for this department
        const approvedResources = allResources.filter(r => r.approvalStatus === 'approved');
        const targetDeptName = deptName?.toUpperCase();
        
        const deptResources = approvedResources.filter(resource => {
          const subject = resource.subject;
          if (!subject) return false;
          const level = subject.level;
          if (!level) return false;
          const degree = level.degree;
          if (!degree) return false;
          const degreeName = degree.name?.toUpperCase();
          
          if (degreeName === targetDeptName) return true;
          if (targetDeptName === 'CPI1' && (degreeName === 'CPI1' || degreeName === 'CYCLE PREPARATOIRE 1')) return true;
          if (targetDeptName === 'CPI2' && (degreeName === 'CPI2' || degreeName === 'CYCLE PREPARATOIRE 2')) return true;
          
          return false;
        });
        
        // Transform resources
        const transformedResources = deptResources.map(r => ({
          id: r.id,
          documentId: r.documentId,
          title: r.title,
          type: r.type,
          createdAt: r.createdAt,
          subject: r.subject,
          file: r.file
        }));
        
        setResources(transformedResources);
        setFilteredResources(transformedResources);
        
        // Get unique levels
        const uniqueLevels = [];
        transformedResources.forEach(resource => {
          const level = resource.subject?.level;
          if (level && !uniqueLevels.find(l => l.id === level.id)) {
            uniqueLevels.push({ id: level.id, name: level.name });
          }
        });
        setLevels(uniqueLevels);
        
      } catch (error) {
        console.error('Error fetching department data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [deptName]);

  // Apply filters
  useEffect(() => {
    let filtered = [...resources];
    
    if (selectedLevel !== 'all') {
      filtered = filtered.filter(resource => {
        const levelId = resource.subject?.level?.id;
        return levelId === selectedLevel;
      });
    }
    
    if (selectedType !== 'all') {
      filtered = filtered.filter(r => r.type?.toUpperCase() === selectedType.toUpperCase());
    }
    
    if (searchTerm) {
      const query = searchTerm.toLowerCase();
      filtered = filtered.filter(r => 
        r.title?.toLowerCase().includes(query) ||
        r.type?.toLowerCase().includes(query)
      );
    }
    
    setFilteredResources(filtered);
  }, [selectedLevel, selectedType, searchTerm, resources]);

  const resourceTypes = [
    { id: 'all', name: 'Tous', icon: <Layers size={12} />, count: resources.length },
    { id: 'cours', name: 'Cours', icon: <BookOpen size={12} />, count: resources.filter(r => r.type?.toUpperCase() === 'COURS').length },
    { id: 'examens', name: 'Examens', icon: <FileText size={12} />, count: resources.filter(r => r.type?.toUpperCase() === 'EXAMENS').length },
    { id: 'tp', name: 'TP', icon: <Zap size={12} />, count: resources.filter(r => r.type?.toUpperCase() === 'TP').length },
    { id: 'td', name: 'TD', icon: <FileCheck size={12} />, count: resources.filter(r => r.type?.toUpperCase() === 'TD').length },
    { id: 'projets', name: 'Projets', icon: <FolderOpen size={12} />, count: resources.filter(r => r.type?.toUpperCase() === 'PROJETS').length },
  ];

  const getTypeIcon = (type) => {
    switch(type?.toUpperCase()) {
      case 'COURS': return <BookOpen size={16} className="text-mint" />;
      case 'EXAMENS': return <FileText size={16} className="text-pink" />;
      case 'TP': return <Zap size={16} className="text-yellow" />;
      case 'TD': return <FileCheck size={16} className="text-orange" />;
      case 'PROJETS': return <FolderOpen size={16} className="text-mint" />;
      default: return <FileText size={16} className="text-white/40" />;
    }
  };

  const getTypeColor = (type) => {
    switch(type?.toUpperCase()) {
      case 'COURS': return 'border-mint/30 text-mint';
      case 'EXAMENS': return 'border-pink/30 text-pink';
      case 'TP': return 'border-yellow/30 text-yellow';
      case 'TD': return 'border-orange/30 text-orange';
      case 'PROJETS': return 'border-mint/30 text-mint';
      default: return 'border-white/20 text-white/40';
    }
  };

  const getSubjectName = (resource) => {
    return resource.subject?.name || 'Général';
  };

  const getLevelName = (resource) => {
    return resource.subject?.level?.name || 'N/A';
  };

  const clearFilters = () => {
    setSelectedLevel('all');
    setSelectedType('all');
    setSearchTerm('');
  };

  const activeFilterCount = [
    selectedLevel !== 'all',
    selectedType !== 'all',
    searchTerm !== ''
  ].filter(Boolean).length;

  if (!department && !loading) {
    return (
      <div className="min-h-screen bg-dark-bg flex items-center justify-center">
        <div className="text-center">
          <div className="text-white/40 text-lg">Département non trouvé</div>
          <Link to="/" className="mt-4 inline-block text-mint hover:text-pink transition">Retour à l'accueil</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-dark-bg min-h-screen">
      {/* Header */}
      <div className="border-b border-white/10 bg-dark-surface/30 backdrop-blur-sm sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Link to="/" className="flex items-center gap-3 sm:gap-4">
  <img 
    src={logo} 
    alt="ISIMM Archive" 
    className="w-10 h-10 sm:w-12 sm:h-12 object-contain"
    style={{ scale: '1.3' }}
  />
  <div>
    <span className="text-lg sm:text-xl font-mono tracking-tighter text-white">ISIMM<span className="gradient-text">_Archive</span></span>
    <p className="font-mono text-[7px] sm:text-[9px] tracking-wider text-white/40 hidden sm:block">digital library</p>
  </div>
</Link>
              <span className="text-white/20">/</span>
              <span className={`font-mono text-sm text-${department?.color}`}>{department?.name}</span>
            </div>
            <Link to="/browse" className="font-mono text-xs text-white/40 hover:text-mint transition flex items-center gap-1"><ArrowLeft size={12} /> Retour</Link>
          </div>
        </div>
      </div>

      {/* Hero Section */}
      <section className="relative pt-20 pb-12 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row gap-8 items-start">
            <div className="text-8xl">{department?.icon}</div>
            <div className="flex-1">
              <h1 className="text-4xl md:text-5xl font-serif font-light text-white">{department?.fullName}<span className={`block text-${department?.color} text-lg font-mono mt-2`}>{department?.name}</span></h1>
              <p className="text-white/40 text-lg mt-4 max-w-2xl">{department?.description}</p>
              <div className="flex flex-wrap gap-6 mt-6">
                <div className="flex items-center gap-2"><Users size={18} className="text-mint" /><span className="text-white/60 text-sm">{department?.students} étudiants</span></div>
                <div className="flex items-center gap-2"><Award size={18} className="text-pink" /><span className="text-white/60 text-sm">{department?.professors} professeurs</span></div>
                <div className="flex items-center gap-2"><Target size={18} className="text-yellow" /><span className="text-white/60 text-sm">{department?.labs} laboratoires</span></div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Filters Section */}
      <section className="px-6 py-8 border-t border-white/10">
        <div className="max-w-7xl mx-auto">
          
          {/* Level Buttons */}
          {levels.length > 0 && (
            <div className="mb-8">
              <h3 className="text-white/60 text-sm font-mono mb-3 flex items-center gap-2">
                <Layers size={14} className="text-mint" />
                FILTRER PAR NIVEAU
              </h3>
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={() => setSelectedLevel('all')}
                  className={`px-4 py-2 rounded-lg font-mono text-xs font-medium transition-all duration-200 ${
                    selectedLevel === 'all'
                      ? `bg-${department?.color} text-dark-bg shadow-md`
                      : 'bg-dark-surface border border-white/10 text-white/60 hover:border-white/30 hover:text-white'
                  }`}
                >
                  Tous les niveaux
                </button>
                {levels.map(level => (
                  <button
                    key={level.id}
                    onClick={() => setSelectedLevel(level.id)}
                    className={`px-4 py-2 rounded-lg font-mono text-xs font-medium transition-all duration-200 ${
                      selectedLevel === level.id
                        ? `bg-${department?.color} text-dark-bg shadow-md`
                        : 'bg-dark-surface border border-white/10 text-white/60 hover:border-white/30 hover:text-white'
                    }`}
                  >
                    {level.name}
                  </button>
                ))}
              </div>
            </div>
          )}
          
          {/* Search and Type Filters */}
          <div className="flex flex-col md:flex-row gap-4 mb-6">
            <div className="flex-1 relative">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/30" />
              <input
                type="text"
                placeholder="Rechercher par titre..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 bg-dark-card border border-white/10 rounded-xl text-white placeholder:text-white/30 focus:outline-none focus:border-mint focus:ring-1 focus:ring-mint transition-all text-sm"
              />
              {searchTerm && (
                <button
                  onClick={() => setSearchTerm('')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-white/30 hover:text-white transition"
                >
                  <X size={14} />
                </button>
              )}
            </div>
            
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`px-4 py-2.5 rounded-xl transition-all duration-200 flex items-center gap-2 ${
                showFilters || activeFilterCount > 0
                  ? `bg-${department?.color} text-dark-bg`
                  : 'bg-dark-card border border-white/10 text-white/60 hover:border-mint/50 hover:text-white'
              }`}
            >
              <Filter size={14} />
              <span className="font-mono text-xs">Types</span>
              {activeFilterCount > 0 && (
                <span className="ml-1 bg-dark-bg text-mint text-[10px] px-1.5 py-0.5 rounded-full">
                  {activeFilterCount}
                </span>
              )}
            </button>
          </div>
          
          {/* Type Filters Panel */}
          {showFilters && (
            <div className="bg-dark-card/50 border border-white/10 rounded-xl p-5 mb-6 animate-fade-up">
              <div className="flex flex-wrap gap-2">
                {resourceTypes.map(type => (
                  <button
                    key={type.id}
                    onClick={() => setSelectedType(type.id)}
                    className={`px-3 py-2 rounded-lg font-mono text-[10px] font-medium transition-all duration-200 flex items-center gap-1.5 ${
                      selectedType === type.id
                        ? `bg-${department?.color} text-dark-bg`
                        : 'bg-dark-surface border border-white/10 text-white/60 hover:border-white/30 hover:text-white'
                    }`}
                  >
                    {type.icon}
                    {type.name}
                    <span className="ml-1 opacity-70 text-[9px]">({type.count})</span>
                  </button>
                ))}
              </div>
              {activeFilterCount > 0 && (
                <div className="mt-4 pt-3 border-t border-white/10 flex justify-end">
                  <button
                    onClick={clearFilters}
                    className="text-white/40 hover:text-mint text-xs font-mono transition flex items-center gap-1"
                  >
                    <X size={12} /> Effacer les filtres
                  </button>
                </div>
              )}
            </div>
          )}
          
          {/* Results Count */}
          <div className="flex justify-between items-center mb-6">
            <div className="text-white/40 text-sm font-mono">
              {filteredResources.length} résultat{filteredResources.length !== 1 ? 's' : ''}
            </div>
            {(selectedLevel !== 'all' || selectedType !== 'all' || searchTerm) && (
              <button
                onClick={clearFilters}
                className="text-white/30 text-xs hover:text-mint transition font-mono"
              >
                Réinitialiser
              </button>
            )}
          </div>
          
          {/* Resources List */}
          {loading ? (
            <div className="text-center py-20">
              <div className="text-white/40">Chargement des ressources...</div>
            </div>
          ) : filteredResources.length === 0 ? (
            <div className="text-center py-20">
              <div className="text-white/40 text-lg">Aucune ressource trouvée</div>
              <div className="text-white/20 text-sm mt-2">Essayez d'autres filtres</div>
            </div>
          ) : (
            <div className="space-y-3">
              {filteredResources.map(resource => (
                <Link
                  key={resource.id}
                  to={`/resource/${resource.documentId}`}
                  className="group bg-dark-surface/50 border border-white/5 rounded-xl p-4 flex items-center justify-between transition-all duration-200 hover:border-mint/30 hover:bg-dark-surface/70"
                >
                  <div className="flex items-center gap-4 flex-1">
                    <div className="w-10 h-10 rounded-xl bg-mint/10 flex items-center justify-center group-hover:bg-mint/20 transition">
                      {getTypeIcon(resource.type)}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-3 flex-wrap">
                        <h3 className="font-mono text-sm text-white group-hover:text-mint transition font-medium">
                          {resource.title}
                        </h3>
                        <span className={`font-mono text-[9px] tracking-wider px-2 py-0.5 rounded border ${getTypeColor(resource.type)}`}>
                          {resource.type || 'Document'}
                        </span>
                      </div>
                      <div className="flex items-center gap-4 mt-2 flex-wrap">
                        <span className="font-mono text-[9px] text-white/30 flex items-center gap-1">
                          <BookOpen size={10} /> {getSubjectName(resource)}
                        </span>
                        <span className="font-mono text-[9px] text-white/30 flex items-center gap-1">
                          <GraduationCap size={10} /> {getLevelName(resource)}
                        </span>
                        <span className="font-mono text-[9px] text-white/30 flex items-center gap-1">
                          <Calendar size={10} /> {resource.createdAt ? new Date(resource.createdAt).toISOString().split('T')[0] : '2026'}
                        </span>
                      </div>
                    </div>
                  </div>
                  <Eye size={16} className="text-white/20 group-hover:text-mint transition" />
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>

      <style>{`
        @keyframes fadeUp {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fade-up {
          animation: fadeUp 0.3s ease-out forwards;
        }
      `}</style>
    </div>
  );
}

export default DepartmentPage;